/**
 * Wallet connections (wagmi).
 *
 * The app offers every browser wallet that announces itself (EIP-6963: MetaMask, Rabby, Coinbase
 * Wallet extension and others, each listed by name) and Base Account, the smart wallet that
 * works without an extension. The wallet is used to sign in and, later, to send transactions on
 * the configured chain.
 */

import type { Chain } from "viem";
import { base, baseSepolia, foundry } from "viem/chains";
import { type Config, createConfig, http } from "wagmi";
import { baseAccount, injected } from "wagmi/connectors";

/** Chains the app can run on, by chain ID. */
const SUPPORTED_CHAINS: Record<number, Chain> = {
  [base.id]: base,
  [baseSepolia.id]: baseSepolia,
  [foundry.id]: foundry,
};

/** How the app names each chain in running text, e.g. "Registered on Base". */
const CHAIN_NAMES: Record<number, string> = {
  [base.id]: "Base",
  [baseSepolia.id]: "Base Sepolia",
  [foundry.id]: "the local chain",
};

/** Display name of a supported chain, for use in a sentence. */
export function chainName(chainId: number): string {
  return CHAIN_NAMES[chainId] ?? `chain ${chainId}`;
}

/** The block explorer page of an address, or `undefined` on a chain without an explorer. */
export function explorerAddressUrl(chainId: number, address: string): string | undefined {
  const explorer = SUPPORTED_CHAINS[chainId]?.blockExplorers?.default.url;
  return explorer ? `${explorer}/address/${address}` : undefined;
}

/** The block explorer page of a transaction, or `undefined` on a chain without an explorer. */
export function explorerTransactionUrl(chainId: number, hash: string): string | undefined {
  const explorer = SUPPORTED_CHAINS[chainId]?.blockExplorers?.default.url;
  return explorer ? `${explorer}/tx/${hash}` : undefined;
}

/**
 * Creates the wagmi configuration for one chain.
 *
 * @throws Error if the chain is not supported.
 */
export function createWagmiConfig(chainId: number): Config {
  const chain = SUPPORTED_CHAINS[chainId];
  if (!chain) {
    throw new Error(`Unsupported chain: ${chainId}`);
  }
  return createConfig({
    chains: [chain],
    connectors: [
      // A fallback for browser wallets that do not announce themselves through EIP-6963.
      injected(),
      // Base Account's telemetry runs an inline script, which the app's Content-Security-Policy
      // refuses. It is turned off rather than allowed.
      baseAccount({ appName: "Morcast", preference: { telemetry: false } }),
    ],
    transports: { [chain.id]: http() },
  });
}
